import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, FileText, X, Search, Zap, CheckCircle, Clock, Activity, ChevronDown, ChevronUp, User, Edit } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const SessionsPage = () => {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(location.search.includes("new=true"));
  const [formData, setFormData] = useState({ patient_id: "", session_type: "followup", date: new Date().toISOString().split("T")[0], notes: "", cost: "", custom_type: "", cost_type: "free" });
  const [search, setSearch] = useState("");
  const [quickMode, setQuickMode] = useState(true);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [pkgForm, setPkgForm] = useState({ plasma: "", meso: "" });

  const fetchData = useCallback(async () => {
    try {
      const sessionsRes = await api.get("/sessions", token);
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error("خطأ في تحميل الجلسات");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load patients only when search is typed (debounced)
  useEffect(() => {
    if (!search || search.length < 2) {
      setPatients([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/patients?search=${encodeURIComponent(search)}`, token);
        setPatients(res.data);
      } catch (err) { console.error(err); }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered patients based on search
  const allQuickPatients = patients
    .filter(p => p.name)
    .map(p => {
      const patientSessions = sessions.filter(s => s.patient_id === p.id);
      const before = p.completed_sessions_before || 0;
      const plasmaDone = before + patientSessions.filter(s => s.session_type === "plasma" || s.session_type === "plasma_meso").length;
      const mesoDone = before + patientSessions.filter(s => s.session_type === "meso" || s.session_type === "plasma_meso").length;
      const plasmaTotal = p.package_plasma_sessions || 0;
      const mesoTotal = p.package_meso_sessions || 0;
      const plasmaRemaining = Math.max(0, plasmaTotal - plasmaDone);
      const mesoRemaining = Math.max(0, mesoTotal - mesoDone);
      const hasPackage = plasmaTotal > 0 || mesoTotal > 0;
      const isComplete = hasPackage && p.sessions_status === "completed";
      return { ...p, plasmaDone, mesoDone, plasmaTotal, mesoTotal, plasmaRemaining, mesoRemaining, isComplete, hasPackage, totalSessions: patientSessions.length };
    })
    .filter(p => !p.isComplete)
    .sort((a, b) => {
      if (a.hasPackage && !b.hasPackage) return -1;
      if (!a.hasPackage && b.hasPackage) return 1;
      return (b.plasmaRemaining + b.mesoRemaining) - (a.plasmaRemaining + a.mesoRemaining);
    });

  const quickAddSession = async (patientId, sessionType) => {
    try {
      await api.post("/sessions", {
        patient_id: patientId,
        session_type: sessionType,
        date: new Date().toISOString().split("T")[0],
        notes: "",
        cost_type: "free",
        cost: null
      }, token);
      const patient = patients.find(p => p.id === patientId);
      const typeLabel = sessionType === "plasma" ? "بلازما" : sessionType === "meso" ? "ميزو" : "بلازما وميزو";
      toast.success(`تم تسجيل جلسة ${typeLabel} لـ ${patient?.name}`);
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("خطأ في تسجيل الجلسة");
    }
  };

  const openPackageEdit = (p) => {
    setEditingPackage(p.id);
    setPkgForm({ plasma: p.plasmaTotal?.toString() || "", meso: p.mesoTotal?.toString() || "" });
    setExpandedPatient(p.id);
  };

  const savePackage = async (patientId) => {
    try {
      await api.put(`/patients/${patientId}`, {
        package_plasma_sessions: pkgForm.plasma ? parseInt(pkgForm.plasma) : 0,
        package_meso_sessions: pkgForm.meso ? parseInt(pkgForm.meso) : 0,
      }, token);
      toast.success("تم تحديث الباقة");
      setEditingPackage(null);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("خطأ في التحديث");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.session_type === "other" && !formData.custom_type) {
      toast.error("يرجى كتابة نوع الجلسة");
      return;
    }
    try {
      await api.post("/sessions", {
        patient_id: formData.patient_id,
        session_type: formData.session_type === "other" ? formData.custom_type : formData.session_type,
        date: formData.date,
        notes: formData.notes,
        cost_type: formData.cost_type,
        cost: formData.cost ? parseFloat(formData.cost) : null
      }, token);
      toast.success("تم إضافة الجلسة بنجاح");
      setShowModal(false);
      setFormData({ patient_id: "", session_type: "followup", date: new Date().toISOString().split("T")[0], notes: "", cost: "", custom_type: "", cost_type: "free" });
      fetchData();
    } catch (error) {
      toast.error("خطأ في إضافة الجلسة");
    }
  };

  const sessionTypeLabels = { 
    followup: "مراجعة", plasma: "بلازما", meso: "ميزو", 
    plasma_meso: "بلازما وميزو", follow_up: "متابعة", other: "أخرى"
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">جاري التحميل...</div></div>;

  return (
    <div className="animate-fade-in" data-testid="sessions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-purple-600" />
            إدارة الجلسات
          </h1>
          <p className="text-gray-500 text-sm mt-1">تسجيل وإدارة جلسات المرضى</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setQuickMode(!quickMode)} 
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${quickMode ? "bg-amber-500 text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            data-testid="toggle-quick-mode"
          >
            <Zap className="w-4 h-4" /> تسجيل سريع
          </button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-1.5 px-4 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-bold hover:bg-purple-700 transition-all shadow-md" data-testid="add-session-global-btn">
            <Plus className="w-4 h-4" /> إضافة جلسة
          </button>
        </div>
      </div>

      {/* Quick Mode - Register sessions without opening patient */}
      {quickMode && (
        <div className="mb-6" data-testid="quick-session-panel">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-amber-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-600" /> تسجيل جلسات سريع
              </h3>
              <span className="text-xs text-amber-600 bg-amber-100 px-3 py-1 rounded-full font-medium">
                {search.length >= 2 ? `${allQuickPatients.length} نتيجة` : "ابحث عن مريض"}
              </span>
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ابحث عن مريض..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-amber-200 bg-white focus:border-amber-400 focus:outline-none text-sm"
                data-testid="quick-search-input"
              />
            </div>

            {/* Patient Cards */}
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {!search || search.length < 2 ? (
                <p className="text-center text-amber-700 py-8 text-sm">اكتب اسم المريض أو رقم هاتفه للبحث...</p>
              ) : allQuickPatients.length === 0 ? (
                <p className="text-center text-gray-500 py-8">لا يوجد نتائج</p>
              ) : allQuickPatients.map(p => (
                <div key={p.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow" data-testid={`quick-patient-${p.id}`}>
                  <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setExpandedPatient(expandedPatient === p.id ? null : p.id)}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${p.hasPackage ? "bg-gradient-to-br from-purple-500 to-purple-700" : "bg-gradient-to-br from-gray-400 to-gray-600"}`}>
                        {p.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{p.name} {p.last_name || ""}</h4>
                        <p className="text-xs text-gray-500">{p.phone} {!p.hasPackage && <span className="text-amber-500 mr-1">(بدون باقة)</span>}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1.5">
                        {/* Patients WITH packages - show remaining */}
                        {p.hasPackage && p.plasmaRemaining > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); quickAddSession(p.id, "plasma"); }}
                            className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-bold hover:bg-purple-200 transition-colors flex items-center gap-1"
                            data-testid={`quick-plasma-${p.id}`}>
                            <Plus className="w-3 h-3" /> بلازما
                            <span className="bg-purple-200 px-1.5 rounded text-[10px]">{p.plasmaDone}/{p.plasmaTotal}</span>
                          </button>
                        )}
                        {p.hasPackage && p.mesoRemaining > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); quickAddSession(p.id, "meso"); }}
                            className="px-3 py-1.5 bg-pink-100 text-pink-700 rounded-lg text-xs font-bold hover:bg-pink-200 transition-colors flex items-center gap-1"
                            data-testid={`quick-meso-${p.id}`}>
                            <Plus className="w-3 h-3" /> ميزو
                            <span className="bg-pink-200 px-1.5 rounded text-[10px]">{p.mesoDone}/{p.mesoTotal}</span>
                          </button>
                        )}
                        {p.hasPackage && p.plasmaRemaining > 0 && p.mesoRemaining > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); quickAddSession(p.id, "plasma_meso"); }}
                            className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-200 transition-colors flex items-center gap-1"
                            data-testid={`quick-both-${p.id}`}>
                            <Plus className="w-3 h-3" /> الاثنين
                          </button>
                        )}
                        {/* Patients WITHOUT packages - show basic add buttons */}
                        {!p.hasPackage && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); quickAddSession(p.id, "plasma"); }}
                              className="px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg text-xs font-bold hover:bg-purple-100 transition-colors flex items-center gap-1"
                              data-testid={`quick-plasma-${p.id}`}>
                              <Plus className="w-3 h-3" /> بلازما
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); quickAddSession(p.id, "meso"); }}
                              className="px-3 py-1.5 bg-pink-50 text-pink-600 rounded-lg text-xs font-bold hover:bg-pink-100 transition-colors flex items-center gap-1"
                              data-testid={`quick-meso-${p.id}`}>
                              <Plus className="w-3 h-3" /> ميزو
                            </button>
                          </>
                        )}
                      </div>
                      {expandedPatient === p.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                  
                  {/* Expanded details */}
                  {expandedPatient === p.id && (
                    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
                      {/* Package Edit Form */}
                      {editingPackage === p.id ? (
                        <div className="bg-white border border-amber-200 rounded-xl p-3 mb-2" data-testid={`pkg-edit-${p.id}`}>
                          <p className="text-xs font-bold text-amber-800 mb-2">تعديل الباقة:</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-[10px] text-gray-500">جلسات بلازما</label>
                              <input type="number" min="0" value={pkgForm.plasma} onChange={(e) => setPkgForm({...pkgForm, plasma: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none" placeholder="0" data-testid={`pkg-plasma-${p.id}`} />
                            </div>
                            <div>
                              <label className="text-[10px] text-gray-500">جلسات ميزو</label>
                              <input type="number" min="0" value={pkgForm.meso} onChange={(e) => setPkgForm({...pkgForm, meso: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-pink-400 focus:outline-none" placeholder="0" data-testid={`pkg-meso-${p.id}`} />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <button onClick={() => savePackage(p.id)} className="flex-1 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700" data-testid={`pkg-save-${p.id}`}>حفظ</button>
                            <button onClick={() => setEditingPackage(null)} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-200">إلغاء</button>
                          </div>
                        </div>
                      ) : p.hasPackage ? (
                        <div className="grid grid-cols-2 gap-3">
                          {p.plasmaTotal > 0 && (
                          <div className="text-center p-2 bg-white rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">بلازما</p>
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-lg font-bold text-purple-600">{p.plasmaDone}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-sm text-gray-600">{p.plasmaTotal}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                              <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${(p.plasmaDone / p.plasmaTotal) * 100}%` }} />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-0.5">متبقي {p.plasmaRemaining}</p>
                          </div>
                        )}
                        {p.mesoTotal > 0 && (
                          <div className="text-center p-2 bg-white rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">ميزوثيرابي</p>
                            <div className="flex items-center justify-center gap-1">
                              <span className="text-lg font-bold text-pink-600">{p.mesoDone}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-sm text-gray-600">{p.mesoTotal}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                              <div className="bg-pink-500 h-1.5 rounded-full" style={{ width: `${(p.mesoDone / p.mesoTotal) * 100}%` }} />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-0.5">متبقي {p.mesoRemaining}</p>
                          </div>
                        )}
                      </div>
                      ) : null}
                      <div className="flex gap-2 mt-2">
                        {editingPackage !== p.id && (
                          <button onClick={() => openPackageEdit(p)} className="flex-1 text-center text-xs text-amber-600 hover:text-amber-800 font-bold py-1.5 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors" data-testid={`edit-pkg-${p.id}`}>
                            {p.hasPackage ? "تعديل الباقة" : "تحديد باقة الجلسات"}
                          </button>
                        )}
                        <button onClick={() => navigate(`/patients/${p.id}`)} className="flex-1 text-center text-xs text-purple-600 hover:text-purple-800 font-medium py-1.5">
                          <User className="w-3 h-3 inline ml-1" /> فتح ملف المريض
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-800">سجل الجلسات ({sessions.length})</h3>
        </div>
        {sessions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-gray-500 font-medium">لا توجد جلسات</h3>
            <p className="text-gray-400 text-sm">ابدأ بإضافة جلسة جديدة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">المريض</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">النوع</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">رقم الجلسة</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">التاريخ</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">التكلفة</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">الحالة</th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice().reverse().map((session) => (
                  <tr 
                    key={session.id} 
                    className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/patients/${session.patient_id}`)}
                    data-testid={`session-row-${session.id}`}
                  >
                    <td className="py-3 px-4 font-medium text-sm">{session.patient_name}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                        session.session_type === "plasma" ? "bg-purple-100 text-purple-700" :
                        session.session_type === "meso" ? "bg-pink-100 text-pink-700" :
                        session.session_type === "plasma_meso" ? "bg-blue-100 text-blue-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {sessionTypeLabels[session.session_type] || session.session_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">#{session.session_number || "-"}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div>{session.date}</div>
                      {session.created_at && <div className="text-[10px] text-gray-400">{new Date(session.created_at).toLocaleString("en-GB", {hour:"2-digit",minute:"2-digit",second:"2-digit"})}</div>}
                    </td>
                    <td className="py-3 px-4 text-sm">{session.cost ? `${session.cost.toLocaleString()}` : <span className="text-green-600 text-xs font-medium">مجانية</span>}</td>
                    <td className="py-3 px-4">
                      <span className={`flex items-center gap-1 text-xs font-medium ${session.status === "completed" ? "text-green-600" : "text-amber-600"}`}>
                        {session.status === "completed" ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                        {session.status === "completed" ? "مكتمل" : "مجدول"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Session Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">إضافة جلسة جديدة</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">المريض *</label>
                  <select className="form-input" value={formData.patient_id} onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })} required data-testid="session-patient-select">
                    <option value="">اختر مريض</option>
                    {patients.filter(p => p.name).map((p) => (
                      <option key={p.id} value={p.id}>{p.name} {p.last_name || ""}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">نوع الجلسة *</label>
                  <select className="form-input" value={formData.session_type} onChange={(e) => setFormData({ ...formData, session_type: e.target.value, custom_type: "" })} data-testid="session-type-select-global">
                    <option value="plasma">بلازما</option>
                    <option value="meso">ميزو</option>
                    <option value="plasma_meso">بلازما وميزو</option>
                    <option value="followup">مراجعة</option>
                    <option value="follow_up">متابعة</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>
                {formData.session_type === "other" && (
                  <div className="form-group">
                    <label className="form-label">نوع الجلسة *</label>
                    <input type="text" className="form-input" value={formData.custom_type} onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })} placeholder="أدخل نوع الجلسة" />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">التاريخ *</label>
                  <input type="date" className="form-input" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid="session-date-input-global" />
                </div>
                <div className="form-group">
                  <label className="form-label">التكلفة</label>
                  <div className="flex gap-2 mb-2">
                    <button type="button" onClick={() => setFormData({...formData, cost_type: "free", cost: ""})} className={`px-3 py-1 rounded-lg text-xs font-bold ${formData.cost_type === "free" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>مجانية</button>
                    <button type="button" onClick={() => setFormData({...formData, cost_type: "paid"})} className={`px-3 py-1 rounded-lg text-xs font-bold ${formData.cost_type === "paid" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>مدفوعة</button>
                  </div>
                  {formData.cost_type === "paid" && (
                    <input type="number" className="form-input" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: e.target.value })} placeholder="المبلغ" />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">ملاحظات</label>
                  <textarea className="form-input" rows={2} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary" data-testid="save-session-global-btn">حفظ الجلسة</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
